/**
 * Game setup: validate deck builds against the budget and assemble the initial
 * GameState — empty board save for each side's King and Prince, decks shuffled
 * deterministically from a seed, and opening hands drawn.
 */
import { buildCost, cardMaxCopies, expandBuild, type CardTypeId, type DeckBuild } from "./cards";
import { toSquare } from "./coords";
import { pieceDef } from "./pieces";
import { shuffle } from "./rng";
import type {
  BoardConfig,
  Card,
  GameState,
  Piece,
  PieceTypeId,
  PlayerId,
  PlayerState,
} from "./types";

export const DEFAULT_CONFIG: BoardConfig = {
  width: 8,
  height: 8,
  budget: 30,
  handLimit: 7,
  openingHand: 3,
  deployRanks: 2,
  maxPlies: 120,
};

export interface PlayerSetup {
  readonly name: string;
  readonly build: DeckBuild;
}

export interface ValidationResult {
  readonly ok: boolean;
  readonly error?: string;
}

export function validateBuild(build: DeckBuild, cfg: BoardConfig): ValidationResult {
  const cost = buildCost(build);
  if (cost > cfg.budget) {
    return { ok: false, error: `Over budget by ${cost - cfg.budget} (${cost}/${cfg.budget}).` };
  }
  for (const [type, count] of Object.entries(build) as [CardTypeId, number][]) {
    const max = cardMaxCopies(type);
    if (max !== undefined && (count ?? 0) > max) {
      return {
        ok: false,
        error: `Too many ${pieceDef(type as PieceTypeId).name}s — max ${max} per deck.`,
      };
    }
  }
  return { ok: true };
}

function royal(type: "king" | "prince", owner: PlayerId): Piece {
  return { id: `${type === "king" ? "k" : "pr"}${owner}`, type, owner, hasMoved: false };
}

/** File positions for the king and prince given board width. */
function royalFiles(width: number): { king: number; prince: number } {
  const king = Math.floor(width / 2);
  return { king, prince: king - 1 };
}

export interface CreateGameOptions {
  readonly id: string;
  readonly seed: number;
  readonly players: readonly [PlayerSetup, PlayerSetup];
  readonly config?: BoardConfig;
}

export function createInitialState(opts: CreateGameOptions): GameState {
  const cfg = opts.config ?? DEFAULT_CONFIG;
  const board: (Piece | null)[] = new Array(cfg.width * cfg.height).fill(null);

  const files = royalFiles(cfg.width);
  const homeRank = (owner: PlayerId) => (owner === 0 ? 0 : cfg.height - 1);
  for (const owner of [0, 1] as const) {
    board[toSquare(files.king, homeRank(owner), cfg.width)] = royal("king", owner);
    board[toSquare(files.prince, homeRank(owner), cfg.width)] = royal("prince", owner);
  }

  let rngState = opts.seed >>> 0;
  const players = ([0, 1] as const).map((owner): PlayerState => {
    const full = expandBuild(owner, opts.players[owner].build);
    const [shuffled, nextState] = shuffle(full, rngState);
    rngState = nextState;
    const hand = shuffled.slice(0, cfg.openingHand) as Card[];
    const deck = shuffled.slice(cfg.openingHand) as Card[];
    return {
      id: owner,
      name: opts.players[owner].name,
      deck,
      hand,
      roster: full as Card[],
      captured: [],
    };
  }) as unknown as [PlayerState, PlayerState];

  return {
    id: opts.id,
    config: cfg,
    board,
    players,
    turn: 0,
    ply: 0,
    idlePlies: 0,
    phase: "playing",
    winner: null,
    drawOffer: null,
    rngState,
    log: [],
  };
}
